<template>
  <div class="location-picker">
    <label class="field-label">
      {{ label }}
      <span v-if="!required" class="optional-label">(Opcional)</span>
    </label>

    <!-- Address search input (Google Places Autocomplete) -->
    <v-text-field
      ref="searchInput"
      v-model="searchQuery"
      placeholder="Buscar dirección o lugar..."
      outlined
      dense
      hide-details="auto"
      prepend-inner-icon="mdi-magnify"
      class="mb-2"
      @keydown.enter.prevent="searchLocation"
    >
      <template v-slot:append>
        <v-btn
          icon
          small
          color="#5E85EF"
          :loading="searching"
          @click="searchLocation"
        >
          <v-icon small>mdi-map-search</v-icon>
        </v-btn>
      </template>
    </v-text-field>

    <!-- Coordinates input -->
    <v-row dense class="mb-2">
      <v-col cols="6">
        <v-text-field
          :value="latitude"
          label="Latitud"
          placeholder="-33.4372"
          outlined
          dense
          hide-details
          type="number"
          step="0.000001"
          @input="updateLatitude"
        />
      </v-col>
      <v-col cols="6">
        <v-text-field
          :value="longitude"
          label="Longitud"
          placeholder="-70.6506"
          outlined
          dense
          hide-details
          type="number"
          step="0.000001"
          @input="updateLongitude"
        />
      </v-col>
    </v-row>

    <!-- Map container -->
    <div class="map-container">
      <div ref="mapEl" class="google-map"></div>

      <!-- Loading overlay -->
      <div v-if="searching || !mapReady" class="map-loading-overlay">
        <v-progress-circular
          indeterminate
          color="#5E85EF"
          size="48"
        />
      </div>
    </div>

    <!-- Helper text -->
    <p class="caption text--secondary mt-1 mb-0">
      <v-icon x-small color="#757575">mdi-information-outline</v-icon>
      Haz clic en el mapa o arrastra el marcador para seleccionar la ubicación
    </p>
  </div>
</template>

<script>
const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-places-script'
let googleMapsLoaderPromise = null

function loadGoogleMapsScript(apiKey) {
  if (window.google && window.google.maps && window.google.maps.places) {
    return Promise.resolve(window.google)
  }

  if (googleMapsLoaderPromise) {
    return googleMapsLoaderPromise
  }

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID)
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google))
      existing.addEventListener('error', reject)
      return
    }

    const script = document.createElement('script')
    script.id = GOOGLE_MAPS_SCRIPT_ID
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=es`
    script.async = true
    script.defer = true
    script.onload = () => resolve(window.google)
    script.onerror = reject
    document.head.appendChild(script)
  })

  return googleMapsLoaderPromise
}

export default {
  name: 'LocationPicker',

  props: {
    label: {
      type: String,
      default: 'Ubicación Geográfica'
    },
    latitude: {
      type: [Number, String],
      default: null
    },
    longitude: {
      type: [Number, String],
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    address: {
      type: String,
      default: null
    },
    apiKey: {
      type: String,
      default: () => process.env.VUE_APP_GOOGLE_API_KEY
    }
  },

  data: () => ({
    searchQuery: '',
    searching: false,
    mapReady: false,
    zoom: 13,
    center: { lat: -33.4372, lng: -70.6506 }, // Santiago, Chile default
    map: null,
    marker: null,
    autocomplete: null
  }),

  computed: {
    markerPosition() {
      if (this.latitude && this.longitude) {
        const lat = parseFloat(this.latitude)
        const lng = parseFloat(this.longitude)
        if (!isNaN(lat) && !isNaN(lng)) {
          return { lat, lng }
        }
      }
      return null
    }
  },

  watch: {
    markerPosition(newPos) {
      if (newPos && this.map) {
        this.center = newPos
        this.setMarkerPosition(newPos)
        this.map.setCenter(newPos)
        this.map.setZoom(15)
      }
    },

    address(newAddress) {
      // Reacciona si el padre llena/cambia la dirección después del mount (ej. llega null y luego async)
      if (newAddress && newAddress !== this.searchQuery) {
        this.searchQuery = newAddress

        // Solo geocodificamos si aún no hay coordenadas; si ya existen, solo mostramos el texto
        if (this.mapReady && !this.markerPosition) {
          this.searchLocation()
        }
      }
    }
  },

  async mounted() {
    if (this.address) {
      this.searchQuery = this.address
    }

    try {
      await loadGoogleMapsScript(this.apiKey)
      this.initMap()
      this.initAutocomplete()
      this.mapReady = true

      // Si se entregó una dirección inicial pero no coordenadas, geocodificarla
      if (this.address && !this.markerPosition) {
        await this.searchLocation()
      }
    } catch (error) {
      console.error('Error loading Google Maps:', error)
      this.$store.snackbarStore.commit('showSnackbar', {
        text: 'Error al cargar Google Maps',
        color: 'error',
        timeout: 3000
      })
    }
  },

  beforeDestroy() {
    if (this.marker) {
      window.google.maps.event.clearInstanceListeners(this.marker)
    }
    if (this.map) {
      window.google.maps.event.clearInstanceListeners(this.map)
    }
    if (this.autocomplete) {
      window.google.maps.event.clearInstanceListeners(this.autocomplete)
    }
  },

  methods: {
    initMap() {
      const google = window.google
      const initialCenter = this.markerPosition || this.center

      this.map = new google.maps.Map(this.$refs.mapEl, {
        center: initialCenter,
        zoom: this.markerPosition ? 15 : this.zoom,
        zoomControl: true,
        scrollwheel: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false
      })

      this.map.addListener('click', this.onMapClick)

      if (this.markerPosition) {
        this.setMarkerPosition(this.markerPosition)
        this.map.setCenter(this.markerPosition)
      }
    },

    initAutocomplete() {
      const google = window.google
      const input = this.$refs.searchInput.$el.querySelector('input')

      this.autocomplete = new google.maps.places.Autocomplete(input, {
        fields: ['geometry', 'formatted_address']
      })
      this.autocomplete.bindTo('bounds', this.map)

      this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete.getPlace()

        if (!place.geometry || !place.geometry.location) {
          this.$store.snackbarStore.commit('showSnackbar', {
            text: 'No se encontró la ubicación',
            color: 'warning',
            timeout: 3000
          })
          return
        }

        const lat = place.geometry.location.lat()
        const lng = place.geometry.location.lng()

        this.applyNewPosition({ lat, lng })

        if (place.geometry.viewport) {
          this.map.fitBounds(place.geometry.viewport)
        } else {
          this.map.setCenter({ lat, lng })
          this.map.setZoom(15)
        }

        if (place.formatted_address) {
          this.searchQuery = place.formatted_address
          this.$emit('update:address', place.formatted_address)
        }
      })
    },

    setMarkerPosition(position) {
      const google = window.google

      if (!this.marker) {
        this.marker = new google.maps.Marker({
          position,
          map: this.map,
          draggable: true,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
              '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 24 24"><path fill="#F4516C" d="M12 2C7.6 2 4 5.6 4 10c0 5.2 8 12 8 12s8-6.8 8-12c0-4.4-3.6-8-8-8zm0 11a3 3 0 110-6 3 3 0 010 6z"/></svg>'
            ),
            scaledSize: new google.maps.Size(32, 42),
            anchor: new google.maps.Point(16, 42)
          }
        })

        this.marker.addListener('dragend', this.onMarkerDrag)
      } else {
        this.marker.setPosition(position)
      }
    },

    applyNewPosition(position) {
      this.setMarkerPosition(position)
      this.$emit('update:latitude', position.lat)
      this.$emit('update:longitude', position.lng)
    },

    updateLatitude(value) {
      this.$emit('update:latitude', value ? parseFloat(value) : null)
    },

    updateLongitude(value) {
      this.$emit('update:longitude', value ? parseFloat(value) : null)
    },

    onMapClick(event) {
      const lat = event.latLng.lat()
      const lng = event.latLng.lng()
      this.applyNewPosition({ lat, lng })
      this.reverseGeocode({ lat, lng })
    },

    onMarkerDrag(event) {
      const lat = event.latLng.lat()
      const lng = event.latLng.lng()
      this.$emit('update:latitude', lat)
      this.$emit('update:longitude', lng)
      this.reverseGeocode({ lat, lng })
    },

    reverseGeocode(position) {
      const google = window.google
      const geocoder = new google.maps.Geocoder()

      geocoder.geocode({ location: position }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          const address = results[0].formatted_address
          this.searchQuery = address
          this.$emit('update:address', address)
        }
      })
    },

    async searchLocation() {
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        return
      }

      this.searching = true
      try {
        const google = window.google
        const geocoder = new google.maps.Geocoder()

        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address: this.searchQuery }, (results, status) => {
            if (status === 'OK' && results && results.length > 0) {
              resolve(results[0])
            } else {
              reject(status)
            }
          })
        })

        const lat = result.geometry.location.lat()
        const lng = result.geometry.location.lng()

        this.applyNewPosition({ lat, lng })
        this.map.setCenter({ lat, lng })
        this.map.setZoom(15)

        if (result.formatted_address) {
          this.searchQuery = result.formatted_address
          this.$emit('update:address', result.formatted_address)
        }
      } catch (error) {
        console.error('Error searching location:', error)
        this.$store.snackbarStore.commit('showSnackbar', {
          text: 'No se encontró la ubicación',
          color: 'warning',
          timeout: 3000
        })
      } finally {
        this.searching = false
      }
    }
  }
}
</script>

<style scoped>
.location-picker {
  width: 100%;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #444;
  margin-bottom: 4px;
}

.optional-label {
  font-weight: 400;
  color: #888;
  font-size: 11px;
}

.map-container {
  position: relative;
  width: 100%;
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #E0E0E0;
}

.google-map {
  width: 100%;
  height: 100%;
}

.map-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
</style>